import React, { useMemo, useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  X,
  User,
  Users,
  MapPin,
  ShoppingBag,
  Trophy,
  CalendarDays,
  LucideIcon,
  Medal,
  Landmark,
} from 'lucide-react';
import { PostAuthorType, getImageUrl } from '../../services/postService';
import { t } from '../../i18n';

// ============================================
// EntityPicker (2026-09-19): wiederverwendbares Auswahlfeld für verwaltete
// Einträge (Spielerprofil/Verein/Spielstätte/Shop/Serie/Turnier/Liga/Verband).
// Extrahiert aus dem PostComposer (Absender-Auswahl): geschlossenes Feld mit
// Avatar + Label, Panel mit Suchfeld, nach Typ gruppiert, große Gruppen
// (> 8) eingeklappt. Neu: Mehrfachauswahl (multiple) mit Chips + Entfernen-X
// und optionale Typ-Einschränkung (allowedTypes). Flaches Design.
// ============================================

const ACCENT = '#3fced1';

/** Gruppen mit mehr Einträgen starten im Auswahl-Panel eingeklappt. */
export const AUTHOR_GROUP_COLLAPSE_THRESHOLD = 8;

export interface ComposerAuthor {
  authorType: PostAuthorType;
  authorId: string;
  name?: string | null;
  /** Avatar: Profilbild des Users bzw. logo ?? image des Listings. */
  image?: string | null;
}

export interface AuthorOption extends ComposerAuthor {
  label: string;
}

export interface AuthorGroup {
  type: PostAuthorType;
  label: string;
  options: AuthorOption[];
}

/** Gruppen-Reihenfolge im Auswahl-Panel (wie in der App). */
export const AUTHOR_GROUP_ORDER: PostAuthorType[] = [
  'PLAYER',
  'CLUB',
  'PUB',
  'SHOP',
  'SERIES',
  'TOURNAMENT',
  'LEAGUE',
  'ASSOCIATION',
];

/** Gruppen-Überschriften (Plural, deutsch). */
export const AUTHOR_GROUP_LABELS: Record<PostAuthorType, string> = {
  PLAYER: 'Spielerprofil',
  CLUB: 'Vereine',
  PUB: t('web.component.footer.spielstaetten'),
  SHOP: 'Shops',
  SERIES: 'Turnierserien',
  TOURNAMENT: 'Turniere',
  LEAGUE: 'Ligen',
  ASSOCIATION: t('web.component.footer.verbaende'),
};

/** Fallback-Icons pro Eintrags-Typ (rund, #3fced1 auf #3fced1/15). */
export const AUTHOR_TYPE_ICONS: Record<PostAuthorType, LucideIcon> = {
  PLAYER: User,
  CLUB: Users,
  PUB: MapPin,
  SHOP: ShoppingBag,
  TOURNAMENT: Trophy,
  SERIES: CalendarDays,
  LEAGUE: Medal,
  ASSOCIATION: Landmark,
};

/** Eindeutiger Schlüssel einer Option: „TYP:id“. */
export const authorOptionKey = (a: ComposerAuthor): string => `${a.authorType}:${a.authorId}`;

/**
 * PURE: Optionen nach Typ gruppieren (feste Reihenfolge: Spielerprofil,
 * Vereine, Spielstätten, Shops, Turnierserien, Turniere, Ligen, Verbände)
 * und optional per Suchtext filtern. Leere Gruppen fallen weg.
 * `allowedTypes` (optional) beschränkt das Ergebnis auf diese Typen –
 * in der Gruppen-Reihenfolge, nicht in der Reihenfolge von allowedTypes.
 */
export function groupAuthorOptions(
  options: AuthorOption[],
  query: string,
  allowedTypes?: PostAuthorType[] | null,
): AuthorGroup[] {
  const q = (query ?? '').trim().toLowerCase();
  const source = Array.isArray(options) ? options : [];
  const allowed = Array.isArray(allowedTypes) ? new Set<PostAuthorType>(allowedTypes) : null;
  const typed = allowed ? source.filter((o) => o?.authorType != null && allowed.has(o.authorType)) : source;
  const filtered = q
    ? typed.filter((o) => (o?.name ?? o?.label ?? '').toLowerCase().includes(q))
    : typed;
  return AUTHOR_GROUP_ORDER.map((type) => ({
    type,
    label: AUTHOR_GROUP_LABELS[type],
    options: filtered.filter((o) => o?.authorType === type),
  })).filter((g) => g.options.length > 0);
}

/** PURE: value (string | string[]) → Liste der gewählten Schlüssel. */
export function normalizePickerValue(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  return typeof value === 'string' && value.length > 0 ? [value] : [];
}

/** Runder Avatar einer Option: Bild oder Typ-Icon als Fallback. */
export const AuthorAvatar: React.FC<{ option: ComposerAuthor; sizeClass?: string }> = ({
  option,
  sizeClass = 'h-8 w-8',
}) => {
  const url = getImageUrl(option.image ?? null);
  const Icon = AUTHOR_TYPE_ICONS[option.authorType] ?? User;
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`${sizeClass} rounded-full object-cover shrink-0 bg-zinc-800`}
      />
    );
  }
  return (
    <span
      className={`${sizeClass} rounded-full flex items-center justify-center shrink-0`}
      style={{ backgroundColor: 'rgba(63,206,209,0.15)' }}
    >
      <Icon size={15} style={{ color: ACCENT }} />
    </span>
  );
};

export interface EntityPickerProps {
  options: AuthorOption[];
  /** Einzelauswahl: „TYP:id“ (oder ''), Mehrfachauswahl: Liste von „TYP:id“. */
  value: string | string[];
  /** Einzelauswahl liefert einen String, Mehrfachauswahl ein Array. */
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  /** Nur diese Typen anbieten (Default: alle). */
  allowedTypes?: PostAuthorType[];
  label: string;
  placeholder: string;
  loading?: boolean;
  /** Text während des Ladens (Default: generisch). */
  loadingText?: string;
  /** Platzhalter des Suchfelds (Default: generisch). */
  searchPlaceholder?: string;
  /** Text bei leerer Trefferliste (Default: generisch). */
  emptyText?: string;
  /** Hilfetext unter dem Feld. */
  helpText?: string;
  /** aria-label des Auswahlfelds (Default: placeholder). */
  ariaLabel?: string;
  /** data-testid des Panels (Default: entity-picker). */
  testId?: string;
}

/**
 * Auswahlfeld für verwaltete Einträge: gruppiert, mit Suche, Collapse ab 8
 * Einträgen je Gruppe. `multiple` → Mehrfachauswahl mit Chips (Entfernen-X);
 * das Panel bleibt dabei offen, bis außerhalb geklickt wird.
 */
const EntityPicker: React.FC<EntityPickerProps> = ({
  options,
  value,
  onChange,
  multiple = false,
  allowedTypes,
  label,
  placeholder,
  loading = false,
  loadingText,
  searchPlaceholder,
  emptyText,
  helpText,
  ariaLabel,
  testId = 'entity-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const selectedKeys = useMemo(() => normalizePickerValue(value), [value]);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const optionByKey = useMemo(() => {
    const map = new Map<string, AuthorOption>();
    (Array.isArray(options) ? options : []).forEach((o) => map.set(authorOptionKey(o), o));
    return map;
  }, [options]);

  const groups = useMemo(
    () => groupAuthorOptions(options, query, allowedTypes),
    [options, query, allowedTypes],
  );
  const searchActive = query.trim().length > 0;

  const selectedSingle = !multiple && selectedKeys[0] ? optionByKey.get(selectedKeys[0]) ?? null : null;

  const openPicker = () => {
    setQuery('');
    // Gruppen mit mehr als 8 Einträgen starten eingeklappt
    const collapsed: Record<string, boolean> = {};
    groupAuthorOptions(options, '', allowedTypes).forEach((g) => {
      collapsed[g.type] = g.options.length > AUTHOR_GROUP_COLLAPSE_THRESHOLD;
    });
    setCollapsedGroups(collapsed);
    setOpen(true);
  };

  const toggleGroup = (type: PostAuthorType) => {
    setCollapsedGroups((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const selectOption = (option: AuthorOption) => {
    const key = authorOptionKey(option);
    if (multiple) {
      const next = selectedSet.has(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key];
      onChange(next);
      return;
    }
    onChange(key);
    setOpen(false);
  };

  const removeKey = (key: string) => {
    if (multiple) onChange(selectedKeys.filter((k) => k !== key));
    else onChange('');
  };

  return (
    <div>
      <p className="block text-sm font-medium text-zinc-300 mb-1.5">{label}</p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-2">
          <Loader2 size={15} className="animate-spin" />
          {loadingText ?? t('web.component.entity-picker.eintraege-werden-geladen')}
        </div>
      ) : (
        <div className="relative">
          {/* Geschlossenes Auswahlfeld: Avatar + Label des gewählten Eintrags */}
          <button
            type="button"
            onClick={openPicker}
            aria-label={ariaLabel ?? placeholder}
            aria-expanded={open}
            className="w-full flex items-center gap-2.5 rounded-xl bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 text-left hover:border-zinc-600 focus:outline-none focus:border-[#3fced1] transition"
          >
            {selectedSingle ? (
              <>
                <AuthorAvatar option={selectedSingle} />
                <span className="flex-1 truncate">{selectedSingle.label}</span>
              </>
            ) : multiple && selectedKeys.length > 0 ? (
              <span className="flex-1 truncate text-zinc-200">
                {t('web.component.entity-picker.ausgewaehlt', { count: selectedKeys.length })}
              </span>
            ) : (
              <span className="flex-1 text-zinc-500">{placeholder}</span>
            )}
            <ChevronDown size={16} className="text-zinc-500 shrink-0" />
          </button>

          {/* Auswahl-Panel: Suche + gruppierte Liste */}
          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
              <div
                className="absolute left-0 right-0 top-full mt-2 z-20 rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl overflow-hidden"
                data-testid={testId}
              >
                {/* Suchfeld oben */}
                <div className="p-2 border-b border-zinc-800">
                  <div className="flex items-center gap-2 rounded-lg bg-zinc-950 border border-zinc-800 px-2.5 py-2 focus-within:border-[#3fced1]">
                    <Search size={14} className="text-zinc-500 shrink-0" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={searchPlaceholder ?? t('web.component.entity-picker.eintraege-suchen')}
                      autoFocus
                      className="w-full bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="max-h-80 overflow-y-auto py-1">
                  {groups.length === 0 && (
                    <p className="px-4 py-3 text-sm text-zinc-500">
                      {emptyText ?? t('web.component.entity-picker.keine-eintraege-gefunden')}
                    </p>
                  )}
                  {groups.map((group) => {
                    // Bei aktiver Suche sind alle Gruppen offen
                    const collapsed = !searchActive && collapsedGroups[group.type] === true;
                    return (
                      <div key={group.type}>
                        {/* Gruppen-Kopf inkl. Anzahl */}
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.type)}
                          className="w-full flex items-center justify-between gap-2 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 hover:text-zinc-300 transition"
                        >
                          <span>
                            {group.label}
                            <span className="ml-1.5 font-medium normal-case">({group.options.length})</span>
                          </span>
                          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {!collapsed &&
                          group.options.map((option) => {
                            const key = authorOptionKey(option);
                            const isSelected = selectedSet.has(key);
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => selectOption(option)}
                                aria-pressed={multiple ? isSelected : undefined}
                                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition hover:bg-zinc-800 ${
                                  isSelected ? 'text-[#3fced1]' : 'text-zinc-200'
                                }`}
                              >
                                <AuthorAvatar option={option} />
                                <span className="flex-1 truncate">{option.label}</span>
                                {multiple && isSelected && <Check size={15} className="shrink-0" style={{ color: ACCENT }} />}
                              </button>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Mehrfachauswahl: Chips der gewählten Einträge mit Entfernen-X */}
      {multiple && selectedKeys.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedKeys.map((key) => {
            const option = optionByKey.get(key) ?? null;
            const chipLabel = option?.label ?? key.split(':').slice(1).join(':');
            return (
              <span
                key={key}
                className="flex items-center gap-1.5 rounded-full border border-[#3fced1]/30 bg-[#3fced1]/10 pl-1 pr-2 py-1 text-sm text-zinc-200"
              >
                {option ? <AuthorAvatar option={option} sizeClass="h-6 w-6" /> : null}
                <span className="truncate max-w-[220px]">{chipLabel}</span>
                <button
                  type="button"
                  onClick={() => removeKey(key)}
                  aria-label={t('web.component.entity-picker.entfernen', { name: chipLabel })}
                  className="text-zinc-400 hover:text-red-400"
                >
                  <X size={13} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {helpText && <p className="mt-1.5 text-xs text-zinc-500">{helpText}</p>}
    </div>
  );
};

export default EntityPicker;
